import { useState, useEffect, useRef, useCallback } from 'react';

// Constants
const SPEED = 5;
const CHARACTER_SIZE = 60;
const GEM_SIZE = 30;
const GEM_SPEED = 2;
const GEM_COUNT = 5;
const GEM_COLLECTION_DISTANCE = 40;
const ENEMY_SIZE = 40;
const INITIAL_ENEMY_COUNT = 1; // Start with just one enemy
const GEMS_PER_NEW_ENEMY = 5; // Add a new enemy every 5 gems
const BASE_ENEMY_SPEED = 1.5;
const ENEMY_SPEED_INCREMENT = 0.1; // Speed increase per gem collected
const MAX_ENEMY_SPEED = 4.0; // Cap on enemy speed
const ENEMY_CATCH_DISTANCE = 50;
const ENEMY_RESPAWN_TIME = 3000; // 3 seconds

export const useGameState = () => {
  const [position, setPosition] = useState({ x: 400, y: 300 });
  const [keys, setKeys] = useState({});
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [isMoving, setIsMoving] = useState(false);
  const [wobbleOffset, setWobbleOffset] = useState(0);
  const [gems, setGems] = useState([]);
  const [collectedGems, setCollectedGems] = useState(0);
  const [enemies, setEnemies] = useState([]);
  const [activeEnemyCount, setActiveEnemyCount] = useState(INITIAL_ENEMY_COUNT);
  const [enemySpeedMultiplier, setEnemySpeedMultiplier] = useState(1.0);
  const [isInvulnerable, setIsInvulnerable] = useState(false);
  const lastMoveTimeRef = useRef(0);
  const animationFrameRef = useRef(0);

  // Initialize gems and enemies when canvas size changes
  useEffect(() => {
    if (canvasSize.width === 0 || canvasSize.height === 0) return;

    // Initialize gems
    const newGems = [];
    for (let i = 0; i < GEM_COUNT; i++) {
      newGems.push({
        id: i,
        x: Math.random() * (canvasSize.width - GEM_SIZE * 2) + GEM_SIZE,
        y: Math.random() * (canvasSize.height - GEM_SIZE * 2) + GEM_SIZE,
        vx: (Math.random() - 0.5) * GEM_SPEED * 2,
        vy: (Math.random() - 0.5) * GEM_SPEED * 2,
        rotation: 0,
        rotationSpeed: (Math.random() * 0.05 + 0.03) * (Math.random() < 0.5 ? -1 : 1) // Faster rotation, random direction
      });
    }
    setGems(newGems);

    // Initialize enemies with just the initial count
    const newEnemies = [];
    for (let i = 0; i < INITIAL_ENEMY_COUNT; i++) {
      newEnemies.push(createEnemy(i, canvasSize));
    }
    setEnemies(newEnemies);
  }, [canvasSize]);

  // Helper function to create a new enemy
  const createEnemy = (id, canvasSize, isActive = true) => {
    // Place enemies at the edges of the screen
    const edge = Math.floor(Math.random() * 4); // 0: top, 1: right, 2: bottom, 3: left
    let x, y;

    switch (edge) {
      case 0: // top
        x = Math.random() * canvasSize.width;
        y = -ENEMY_SIZE;
        break;
      case 1: // right
        x = canvasSize.width + ENEMY_SIZE;
        y = Math.random() * canvasSize.height;
        break;
      case 2: // bottom
        x = Math.random() * canvasSize.width;
        y = canvasSize.height + ENEMY_SIZE;
        break;
      case 3: // left
        x = -ENEMY_SIZE;
        y = Math.random() * canvasSize.height;
        break;
    }

    // Generate a color based on the enemy ID
    const colors = ['#e53e3e', '#dd6b20', '#805ad5', '#38a169', '#3182ce', '#d53f8c', '#9f7aea', '#667eea'];
    const colorIndex = id % colors.length;

    return {
      id,
      x,
      y,
      baseSpeed: BASE_ENEMY_SPEED * (0.8 + Math.random() * 0.4), // Slight speed variation
      color: colors[colorIndex],
      active: isActive,
      wiggle: Math.random() * Math.PI * 2 // Random starting phase for wiggle
    };
  };

  // Update active enemy count based on collected gems
  useEffect(() => {
    const newActiveCount = INITIAL_ENEMY_COUNT + Math.floor(collectedGems / GEMS_PER_NEW_ENEMY);

    if (newActiveCount !== activeEnemyCount) {
      setActiveEnemyCount(newActiveCount);

      // Check if we need to add more enemies
      setEnemies(prevEnemies => {
        let updatedEnemies = [...prevEnemies];

        // If we need more enemies than we currently have, create new ones
        if (newActiveCount > updatedEnemies.length) {
          const enemiesToAdd = newActiveCount - updatedEnemies.length;

          for (let i = 0; i < enemiesToAdd; i++) {
            const newEnemyId = updatedEnemies.length;
            updatedEnemies.push(createEnemy(newEnemyId, canvasSize));
          }
        }
        // Otherwise, activate existing inactive enemies
        else {
          updatedEnemies = updatedEnemies.map((enemy, index) => {
            if (index < newActiveCount && !enemy.active && !isInvulnerable) {
              // Reposition at a random edge
              const newEnemy = createEnemy(enemy.id, canvasSize);
              return {
                ...enemy,
                x: newEnemy.x,
                y: newEnemy.y,
                active: true
              };
            }
            return enemy;
          });
        }

        return updatedEnemies;
      });
    }
  }, [collectedGems, activeEnemyCount, canvasSize, isInvulnerable]);

  // Handle key down event
  const handleKeyDown = useCallback((e) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd'].includes(e.key)) {
      e.preventDefault();
      setKeys((prevKeys) => ({ ...prevKeys, [e.key]: true }));
      setIsMoving(true);
      lastMoveTimeRef.current = Date.now();
    }
  }, []);

  // Handle key up event
  const handleKeyUp = useCallback((e) => {
    setKeys((prevKeys) => ({ ...prevKeys, [e.key]: false }));

    // Check if any movement keys are still pressed
    setTimeout(() => {
      const anyMovementKeyPressed = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd']
        .some(key => keys[key]);

      if (!anyMovementKeyPressed) {
        setIsMoving(false);
      }
    }, 10);
  }, [keys]);

  // Handle character movement, gem collection, and enemy movement
  useEffect(() => {
    const gameLoop = setInterval(() => {
      // Update player position
      setPosition((prevPos) => {
        let newX = prevPos.x;
        let newY = prevPos.y;
        let moved = false;

        if (keys['ArrowUp'] || keys['w']) {
          newY -= SPEED;
          moved = true;
        }
        if (keys['ArrowDown'] || keys['s']) {
          newY += SPEED;
          moved = true;
        }
        if (keys['ArrowLeft'] || keys['a']) {
          newX -= SPEED;
          moved = true;
        }
        if (keys['ArrowRight'] || keys['d']) {
          newX += SPEED;
          moved = true;
        }

        // Update last move time if character moved
        if (moved) {
          lastMoveTimeRef.current = Date.now();
        } else if (Date.now() - lastMoveTimeRef.current > 100) {
          setIsMoving(false);
        }

        // Boundary checks
        newX = Math.max(CHARACTER_SIZE/2, Math.min(canvasSize.width - CHARACTER_SIZE/2, newX));
        newY = Math.max(CHARACTER_SIZE/2, Math.min(canvasSize.height - CHARACTER_SIZE/2, newY));

        return { x: newX, y: newY };
      });

      // Update gems
      setGems(prevGems => {
        let gemsCollected = 0;

        const updatedGems = prevGems.map(gem => {
          // Check if player collected this gem
          const dx = position.x - gem.x;
          const dy = position.y - gem.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < GEM_COLLECTION_DISTANCE) {
            gemsCollected++;

            // Increase enemy speed when a gem is collected
            setEnemySpeedMultiplier(prev => {
              const newMultiplier = prev + ENEMY_SPEED_INCREMENT;
              // Cap the speed multiplier to prevent enemies from becoming too fast
              return Math.min(newMultiplier, MAX_ENEMY_SPEED / BASE_ENEMY_SPEED);
            });

            // Respawn gem at a random position
            return {
              ...gem,
              x: Math.random() * (canvasSize.width - GEM_SIZE * 2) + GEM_SIZE,
              y: Math.random() * (canvasSize.height - GEM_SIZE * 2) + GEM_SIZE,
              vx: (Math.random() - 0.5) * GEM_SPEED * 2,
              vy: (Math.random() - 0.5) * GEM_SPEED * 2,
              rotation: 0,
              rotationSpeed: (Math.random() * 0.05 + 0.03) * (Math.random() < 0.5 ? -1 : 1) // Faster rotation for respawned gems
            };
          }

          // Update gem position
          let newX = gem.x + gem.vx;
          let newY = gem.y + gem.vy;
          let newVx = gem.vx;
          let newVy = gem.vy;
          let newRotation = gem.rotation + gem.rotationSpeed;

          // Bounce off walls
          if (newX < GEM_SIZE/2 || newX > canvasSize.width - GEM_SIZE/2) {
            newVx = -newVx;
            newX = Math.max(GEM_SIZE/2, Math.min(canvasSize.width - GEM_SIZE/2, newX));
          }
          if (newY < GEM_SIZE/2 || newY > canvasSize.height - GEM_SIZE/2) {
            newVy = -newVy;
            newY = Math.max(GEM_SIZE/2, Math.min(canvasSize.height - GEM_SIZE/2, newY));
          }

          // Add this code to make gems avoid the player
          if (distance < 100 && distance > GEM_COLLECTION_DISTANCE) {
            // Calculate repulsion vector (away from player)
            const repelX = (gem.x - position.x) * 0.01;
            const repelY = (gem.y - position.y) * 0.01;

            // Apply repulsion to velocity
            newVx += repelX;
            newVy += repelY;

            // Limit max velocity
            const speed = Math.sqrt(newVx * newVx + newVy * newVy);
            if (speed > GEM_SPEED * 2) {
              newVx = (newVx / speed) * GEM_SPEED * 2;
              newVy = (newVy / speed) * GEM_SPEED * 2;
            }
          }

          return {
            ...gem,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy,
            rotation: newRotation
          };
        });

        if (gemsCollected > 0) {
          setCollectedGems(prev => prev + gemsCollected);
        }

        return updatedGems;
      });

      // Update enemies
      setEnemies(prevEnemies => {
        let playerCaught = false;

        const updatedEnemies = prevEnemies.map((enemy, index) => {
          // Skip inactive enemies
          if (!enemy.active) {
            // Check if it's time to respawn
            if (enemy.respawnTime && Date.now() - enemy.respawnTime > ENEMY_RESPAWN_TIME) {
              // Only respawn if this enemy should be active based on current activeEnemyCount
              if (index < activeEnemyCount) {
                // Create a new enemy at a random edge
                const newEnemy = createEnemy(enemy.id, canvasSize);
                return {
                  ...enemy,
                  x: newEnemy.x,
                  y: newEnemy.y,
                  active: true,
                  respawnTime: null
                };
              }
            }
            return enemy;
          }

          // Update enemy position to chase player
          const dx = position.x - enemy.x;
          const dy = position.y - enemy.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Check if enemy caught player
          if (distance < ENEMY_CATCH_DISTANCE && !isInvulnerable) {
            playerCaught = true;
          }

          // Move enemy towards player with some wiggle
          const angle = Math.atan2(dy, dx);
          const wiggleAmount = Math.sin(enemy.wiggle) * 0.2;
          const moveAngle = angle + wiggleAmount;

          // Calculate current speed with multiplier
          const currentSpeed = enemy.baseSpeed * enemySpeedMultiplier;

          // Update wiggle phase
          const newWiggle = enemy.wiggle + 0.1;

          return {
            ...enemy,
            x: enemy.x + Math.cos(moveAngle) * currentSpeed,
            y: enemy.y + Math.sin(moveAngle) * currentSpeed,
            wiggle: newWiggle
          };
        });

        // If player was caught, reset gems and make player temporarily invulnerable
        if (playerCaught && !isInvulnerable) {
          // Save high score to localStorage
          const storedHighScore = parseInt(localStorage.getItem('tyb_high_score') || '0');
          if (collectedGems > storedHighScore) {
            localStorage.setItem('tyb_high_score', collectedGems.toString());
          }

          // Send gem count to server
          fetch('/api/v1/user_rewards/update_gem_count', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
            },
            body: JSON.stringify({ gem_count: collectedGems })
          })
          .then(response => response.json())
          .then(data => {
            console.log('Gem count saved:', data);
          })
          .catch(error => {
            console.error('Error saving gem count:', error);
          });

          setCollectedGems(0);
          setIsInvulnerable(true);

          // Reset enemy speed multiplier when caught
          setEnemySpeedMultiplier(1.0);

          // Reset active enemy count to initial
          setActiveEnemyCount(INITIAL_ENEMY_COUNT);

          // Reset enemies to edges
          const resetEnemies = updatedEnemies.map((enemy, index) => ({
            ...enemy,
            active: index < INITIAL_ENEMY_COUNT ? false : false, // Keep only initial enemies, deactivate the rest
            respawnTime: Date.now()
          }));

          // Set timeout to remove invulnerability
          setTimeout(() => {
            setIsInvulnerable(false);
          }, 3000);

          return resetEnemies;
        }

        return updatedEnemies;
      });
    }, 1000 / 60); // 60 FPS

    return () => clearInterval(gameLoop);
  }, [keys, position, canvasSize, isInvulnerable, enemySpeedMultiplier, activeEnemyCount]);

  // South Park style wobble animation
  useEffect(() => {
    const wobbleAnimation = () => {
      if (isMoving) {
        setWobbleOffset(prev => {
          // Oscillate between -5 and 5
          const newOffset = prev + 0.5;
          return newOffset > 5 ? -5 : newOffset;
        });
      } else {
        // Reset wobble when not moving
        setWobbleOffset(0);
      }

      animationFrameRef.current = requestAnimationFrame(wobbleAnimation);
    };

    animationFrameRef.current = requestAnimationFrame(wobbleAnimation);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isMoving]);

  return {
    position,
    setPosition,
    keys,
    setKeys,
    canvasSize,
    setCanvasSize,
    isMoving,
    setIsMoving,
    wobbleOffset,
    gems,
    collectedGems,
    enemies,
    activeEnemyCount,
    enemySpeedMultiplier,
    isInvulnerable,
    handleKeyDown,
    handleKeyUp
  };
};